import type { PlanAction, PlanNodeOut } from '@/client';
import { addDays, format, parseISO } from 'date-fns';
import { walk } from '@/components/maintenance/model/planSelectors';
import { type FrequencySelection, toFrequencySelection } from '@/utils/maintenance/frequencyUtils';
import {
	DISMISS_NOTE_MAX_LENGTH,
	type DismissReason,
	type PlanEditableField,
	type PlanPlacement,
	PlanActions,
} from '@/components/maintenance/model/planVocabulary';

/**
 * What the plan's one write endpoint takes, and what a card can actually be asked to do.
 *
 * `POST /v2/houses/{id}/maintenance/actions` is one endpoint per verb rather than a REST resource:
 * the card is named by echoing back the opaque `resolve_token` the plan handed out, so a client
 * never assembles an anchor from parts and never addresses a URL per card. Everything that varies
 * between verbs is the `params` object, and this module is the single place that knows which verb
 * takes which — the drawer builds a request here and posts it, rather than each sheet inventing a
 * body of its own.
 */

/** A week is always seven days; the endpoint has no `interval_weeks`, so weekly cadences go as days. */
const DAYS_PER_WEEK = 7;

/**
 * Actions the endpoint resolves against a dated occurrence rather than the card as a whole.
 *
 * Mirrors the backend's `_OCCURRENCE_ACTIONS`. A card whose `action_target` carries no
 * `occurrence_bucket` has no occurrence to resolve, and the endpoint answers one of these with a
 * 422 — so this set is what lets the drawer decline to offer the button instead of letting the user
 * press it and be refused. Keep it in step with the backend set; a mismatch shows up as either a
 * dead button or a hidden one.
 */
export const OCCURRENCE_SCOPED_ACTIONS: ReadonlySet<PlanAction> = new Set<PlanAction>([
	PlanActions.Complete,
	PlanActions.BulkComplete,
	PlanActions.Snooze,
	PlanActions.Skip,
	PlanActions.Note,
]);

/**
 * The verbs that end an occasion, rather than recording something against it.
 *
 * A subset of {@link OCCURRENCE_SCOPED_ACTIONS}, and deliberately not all of it: `note` is
 * occurrence-scoped too, but filing one against a task already done is an ordinary thing to want —
 * an invoice that arrived a week late belongs on the occasion it belongs to, and on a settled card
 * opened as itself the write lands on exactly that occasion. These are the ones that answer "is
 * this dealt with", so a card whose status is already settled must not offer them however its
 * `allowed_actions` reads.
 */
export const CLOSE_OUT_ACTIONS: ReadonlySet<PlanAction> = new Set<PlanAction>([
	PlanActions.Complete,
	PlanActions.BulkComplete,
	PlanActions.Skip,
	PlanActions.Snooze,
]);

/**
 * Everything withheld while a finished occasion is being *read* — reached through History.
 *
 * Wider than {@link CLOSE_OUT_ACTIONS} because the route changes what the page is. Opened as
 * itself, a settled card is the card, and a late note belongs on it. Opened from a History row, the
 * page is an account of one day that has already happened, and three more things stop making sense:
 *
 * - `edit`, `reschedule` and the removal verbs (`delete`, `dismiss`, `bulk_dismiss`) would land
 *   correctly and still be the wrong offer. Rewording or moving the current series from an account of
 *   a finished day says nothing about that day, and deleting the card from there would take the
 *   account with it. Changing the cadence is the same
 *   move under another name, so the frequency chip goes quiet too.
 * - `note` is the one that hurts, and it is withheld for a mechanical reason rather than a
 *   editorial one: the write goes to `node.action_target.resolve_token`, which names the card's
 *   current occasion. Where that is a *different* day from the one on screen the note lands on the
 *   wrong occasion silently, so it cannot be offered here at all.
 *
 * With everything withheld the footer has nothing to draw and falls away on its own — except where
 * a draft is unsaved, which is the one control on that view about something the household did.
 *
 * TODO(schedule/occurrence-scoped-note): pass `occurrences[].resolve_token` through to the write —
 * `planActionRequest` already takes the override — then `note` can come back out of here and a late
 * invoice can be filed from the row that shows the day it belongs to.
 */
export const READ_OCCASION_WITHHELD_ACTIONS: ReadonlySet<PlanAction> = new Set<PlanAction>([
	...OCCURRENCE_SCOPED_ACTIONS,
	PlanActions.Reschedule,
	PlanActions.Edit,
	PlanActions.Delete,
	PlanActions.Dismiss,
	PlanActions.BulkDismiss,
]);

export interface PlanActionRequest {
	resolve_token: string;
	action: PlanAction;
	params: Record<string, string | number>;
	client_token: string;
}

/** The cadence as the endpoint's one-of-three interval fields; weeks fold into days, exactly. */
const intervalParams = ({ every, interval }: FrequencySelection): Record<string, number> => {
	switch (interval) {
		case 'day':
			return { interval_days: every };
		case 'week':
			return { interval_days: every * DAYS_PER_WEEK };
		case 'month':
			return { interval_months: every };
		case 'year':
			return { interval_years: every };
	}
};

/**
 * The verbs the drawer sends, each with the parameters that verb alone takes.
 *
 * A discriminated union rather than a loose `params` bag, so a sheet cannot send `until` on a
 * complete or forget it on a snooze — the two mistakes this endpoint answers with a 422 rather than
 * a type error.
 *
 * `reschedule` covers two different sheets, which is the endpoint's shape rather than a shortcut:
 * moving one date is an `anchor_date`, changing how often a card repeats is an interval, and both
 * are the same obligation-level write.
 */
export type PlanActionIntent =
	| { action: typeof PlanActions.Complete; completedAt?: string }
	| { action: typeof PlanActions.BulkComplete; completedAt?: string }
	/**
	 * The undo verbs take no parameters — they name an occasion and withdraw what was recorded
	 * against it. The backend advertises whichever half applies, so a card offering `uncomplete` is
	 * one that has been completed.
	 */
	| { action: typeof PlanActions.Uncomplete }
	| { action: typeof PlanActions.Unaccept }
	| { action: typeof PlanActions.Unkeep }
	| { action: typeof PlanActions.ClearReschedule }
	| { action: typeof PlanActions.Skip }
	| { action: typeof PlanActions.Snooze; until: string }
	| { action: typeof PlanActions.Note; text: string }
	/**
	 * Keeping a card also says which surface it lands on. Omitting it is not the same as choosing:
	 * the server resolves its own default from the card's domain, and only a household election
	 * travels here.
	 *
	 * Repeating `keep` moves an already-kept card. A passed General one-off can carry its replacement
	 * date on the same write, so promoting it does not immediately create an overdue item.
	 *
	 * A cadence rides here for the same reason a placement does: correcting how often a card repeats
	 * and then keeping it is one decision, and the endpoint takes both in one transaction, so a
	 * half-failure cannot leave a card rescheduled but unkept.
	 */
	| { action: typeof PlanActions.Keep; placement?: PlanPlacement; anchorDate?: string; cadence?: string }
	/**
	 * The two ways to turn a card down, which are not one verb with a scope: a leaf advertises
	 * `dismiss`, a group advertises `bulk_dismiss` and never the plain form. `dismissActionFor` reads
	 * which off the card rather than deciding it here.
	 *
	 * Either may carry why, and either may omit it — a plain dismiss is unchanged. The reason is
	 * captured for analysis, never folded back onto the card, which is hidden the moment it lands.
	 */
	| { action: typeof PlanActions.Dismiss; reason?: DismissReason; reasonNote?: string }
	| { action: typeof PlanActions.BulkDismiss; reason?: DismissReason; reasonNote?: string }
	| { action: typeof PlanActions.Restore }
	| { action: typeof PlanActions.Delete }
	| { action: typeof PlanActions.Reschedule; anchorDate: string }
	| { action: typeof PlanActions.Reschedule; cadence: string }
	| { action: typeof PlanActions.Edit; field: PlanEditableField; value: string };

/** A write that settles the occurrence currently represented by a schedule row. */
export type PlanSettlementAction =
	| typeof PlanActions.Complete
	| typeof PlanActions.BulkComplete
	| typeof PlanActions.Skip
	| typeof PlanActions.Delete;

interface PendingPlanTransitionBase {
	nodeId: string;
	occurrenceStableKey: string | null;
	/** The top-level card as accepted, retained until its visual exit finishes. */
	displayRoot: PlanNodeOut;
	/** Its position in the plan when accepted, so an early refresh cannot move the snapshot. */
	displayRootIndex?: number;
	/** Confirm the write, exit the old place, suppress stale data, then introduce the new place. */
	phase: 'confirmed' | 'exiting' | 'hidden' | 'entering';
}

/**
 * The occurrence whose accepted write is waiting for the plan read model to catch up.
 *
 * `node_id` identifies the obligation and deliberately survives rematerialisation. The occurrence
 * key identifies the particular dated instance that was answered, so a recurring task stops
 * settling as soon as the endpoint returns its next instance under the same node id.
 */
export interface PendingPlanSettlement extends PendingPlanTransitionBase {
	action: PlanSettlementAction;
}

/** A snoozed occurrence moving between two dates without becoming a History outcome. */
export interface PendingPlanMove extends PendingPlanTransitionBase {
	action: typeof PlanActions.Snooze;
	movedTo: string;
}

export type PendingPlanTransition = PendingPlanSettlement | PendingPlanMove;

const settlementOccurrenceKey = (node: PlanNodeOut): string | null => {
	const ownKey = node.action_target?.occurrence_stable_key;
	if (ownKey) return ownKey;
	const childKeys = (node.children ?? [])
		.map(settlementOccurrenceKey)
		.filter((key): key is string => key !== null)
		.sort();
	return childKeys.length > 0 ? `children:${childKeys.join('|')}` : null;
};

export const pendingPlanSettlement = (
	node: PlanNodeOut,
	action: PlanSettlementAction,
	displayRoot: PlanNodeOut = node,
	displayRootIndex?: number,
): PendingPlanSettlement => ({
	nodeId: node.node_id,
	occurrenceStableKey: settlementOccurrenceKey(node),
	action,
	displayRoot,
	displayRootIndex,
	phase: 'confirmed',
});

export const pendingPlanMove = (
	node: PlanNodeOut,
	movedTo: string,
	displayRoot: PlanNodeOut = node,
	displayRootIndex?: number,
): PendingPlanMove => ({
	nodeId: node.node_id,
	occurrenceStableKey: settlementOccurrenceKey(node),
	action: PlanActions.Snooze,
	movedTo,
	displayRoot,
	displayRootIndex,
	phase: 'confirmed',
});

/**
 * Keeps accepted occurrences mounted until their exit beat finishes, even when a faster plan refresh
 * has already removed a one-off or replaced a recurring card with its successor.
 */
export const withPlanTransitionSnapshots = (
	nodes: PlanNodeOut[],
	transitions: readonly PendingPlanTransition[],
): PlanNodeOut[] => {
	const snapshots = new Map(
		transitions
			.filter(({ phase }) => phase === 'confirmed' || phase === 'exiting')
			.map(({ displayRoot, displayRootIndex }) => [displayRoot.node_id, { node: displayRoot, displayRootIndex }]),
	);
	if (snapshots.size === 0) return nodes;

	// Every settling card is placed by where it *was*, never by where the refreshed plan puts it.
	// The two cases look different and are not: a one-off is dropped from the read, while a recurring
	// obligation comes back under the same `node_id` carrying its successor's date — and because the
	// endpoint sorts by `next_due`, that successor sits near the end. Swapping the snapshot in at that
	// position filed a card still showing today's date at the bottom of today, so it slid the length
	// of the day before leaving. Lifting both out and splicing by the remembered index holds it still.
	const retained = nodes.filter((node) => !snapshots.has(node.node_id));
	for (const snapshot of [...snapshots.values()].sort(
		(a, b) => (a.displayRootIndex ?? Number.MAX_SAFE_INTEGER) - (b.displayRootIndex ?? Number.MAX_SAFE_INTEGER),
	)) {
		const index = Math.min(snapshot.displayRootIndex ?? retained.length, retained.length);
		retained.splice(index, 0, snapshot.node);
	}
	return retained;
};

/**
 * Visible phases derived from pending writes and the newest plan in the same render.
 *
 * Once an exit finishes, admit an already-present recurring successor immediately. Waiting for an
 * effect produced one painted frame with neither occurrence, moving the schedule up and then down.
 */
export const planTransitionDisplayState = (
	transitions: readonly PendingPlanTransition[],
	nodesById: ReadonlyMap<string, PlanNodeOut>,
) => {
	const actions = new Map<string, PlanSettlementAction>();
	const exiting = new Set<string>();
	const hidden = new Set<string>();
	const entering = new Set<string>();

	for (const transition of transitions) {
		const node = nodesById.get(transition.nodeId);
		const successorIsReady =
			transition.phase === 'hidden' && node !== undefined && !isPlanTransitionCurrent(transition, node);

		if (
			transition.action !== PlanActions.Snooze &&
			(transition.phase === 'confirmed' || transition.phase === 'exiting')
		) {
			actions.set(transition.nodeId, transition.action);
		}
		if (transition.phase === 'exiting') exiting.add(transition.nodeId);
		if (transition.phase === 'hidden' && !successorIsReady) hidden.add(transition.nodeId);
		if (transition.phase === 'entering' || successorIsReady) entering.add(transition.nodeId);
	}

	return {
		settlementActionByNodeId: actions,
		exitingNodeIds: exiting,
		hiddenNodeIds: hidden,
		enteringNodeIds: entering,
	};
};

/** Whether the plan still represents the occurrence whose write already landed. */
export const isPlanTransitionCurrent = (transition: PendingPlanTransition, node: PlanNodeOut | undefined): boolean => {
	if (!node || node.node_id !== transition.nodeId) return false;
	// The next regular occurrence can precede the deferred date. It is fresh work, not the stale
	// occurrence we are hiding, so admit it even though its date does not match the snooze destination.
	if (transition.occurrenceStableKey && settlementOccurrenceKey(node) !== transition.occurrenceStableKey) {
		return false;
	}
	// A snooze moves the same bucket, so its occurrence key deliberately does not change. Its new date
	// is the evidence that the plan projection has caught up.
	if (transition.action === PlanActions.Snooze) return node.next_due !== transition.movedTo;

	// A genuinely keyless one-off has no later occurrence to distinguish. Keep its stale row hidden
	// until the Upcoming read removes the node; treating a changed action set as a new occurrence made
	// completed custom tasks reappear permanently in the forward schedule.
	return true;
};

/** The first date that honestly means “defer”: strictly after today and the current occurrence. */
export const minimumSnoozeDate = (node: PlanNodeOut, today: string): string => {
	const after = [node.action_target?.target_date, node.next_due].reduce<string>(
		(latest, date) => (date && date > latest ? date : latest),
		today,
	);
	return format(addDays(parseISO(after), 1), 'yyyy-MM-dd');
};

export const canSnoozeUntil = (node: PlanNodeOut, until: string, today: string): boolean =>
	until !== '' && until >= minimumSnoozeDate(node, today);

/**
 * The optional why on a dismiss, omitted entirely when it was not given.
 *
 * An empty `reason` is not the same as none: the endpoint validates whatever arrives against its
 * closed vocabulary and answers 422, so sending `''` turns a plain dismiss into a refused one. The
 * note is trimmed and length-capped here as well as there — the endpoint would truncate silently,
 * and a household that typed six hundred characters should not find four hundred of them gone.
 */
const dismissParams = (intent: { reason?: DismissReason; reasonNote?: string }): Record<string, string> => {
	const note = intent.reasonNote?.trim().slice(0, DISMISS_NOTE_MAX_LENGTH);
	return {
		...(intent.reason ? { reason: intent.reason } : {}),
		...(note ? { reason_note: note } : {}),
	};
};

/**
 * The verb's parameters, or `null` when the intent cannot be expressed as any.
 *
 * `reschedule` is the only verb that can fail here. The endpoint needs at least one interval or an
 * anchor date and refuses an empty one, so a cadence no interval describes has to stop the request
 * rather than send `{}` and be refused. "Doesn't repeat" is the case that reaches it: the selector
 * emits `once`, and no number of days, months or years says "never again".
 */
const paramsFor = (intent: PlanActionIntent): Record<string, string | number> | null => {
	switch (intent.action) {
		case PlanActions.Keep: {
			// A cadence no interval expresses ("doesn't repeat") is dropped rather than sent: the keep
			// still stands, and there is no param that says never again.
			const cadence = intent.cadence ? toFrequencySelection(intent.cadence) : null;
			return {
				...(intent.placement ? { placement: intent.placement } : {}),
				...(intent.anchorDate ? { anchor_date: intent.anchorDate } : {}),
				...(cadence ? intervalParams(cadence) : {}),
			};
		}
		case PlanActions.Complete:
		case PlanActions.BulkComplete:
			return intent.completedAt ? { completed_at: intent.completedAt } : {};
		case PlanActions.Snooze:
			return { until: intent.until };
		case PlanActions.Note:
			return { text: intent.text };
		case PlanActions.Edit:
			return { field: intent.field, value: intent.value };
		case PlanActions.Dismiss:
		case PlanActions.BulkDismiss:
			return dismissParams(intent);
		case PlanActions.Reschedule: {
			if ('anchorDate' in intent) return { anchor_date: intent.anchorDate };
			const selection = toFrequencySelection(intent.cadence);
			return selection ? intervalParams(selection) : null;
		}
		default:
			return {};
	}
};

/**
 * Whether a card can be asked to do this, judged from the card itself.
 *
 * `allowed_actions` is the whole answer. The backend prunes it to what is meaningful in the card's
 * current state — the undo verb in place of its forward one, `restore` only on a dismissed card,
 * `accept` only on one awaiting review — so what is advertised is a subset of what is enforced.
 * Re-deriving any of that here would only be a second opinion that can disagree.
 */
/**
 * Whether this reader may deliver a review verdict on this plan at all.
 *
 * A verdict is a `keep`, and `allowed_actions` arrives pruned to the verbs the reader may send — so
 * a plan in which nothing advertises `keep` is a plan this reader cannot review. Reads the raw
 * capability rather than going through `canSendPlanAction`, whose already-kept shortcut answers true
 * for a card that is settled and would let a reader who may not keep past this question.
 */
export const canReviewPlan = (nodes: readonly PlanNodeOut[]): boolean =>
	[...walk(nodes)].some((node) => Boolean(node.allowed_actions?.includes(PlanActions.Keep)));

export const canSendPlanAction = (node: PlanNodeOut, action: PlanAction): boolean => {
	// `keep` remains the placement write after the review verdict has landed, but kept cards do not
	// keep advertising the verdict action. The backend explicitly accepts that repeated keep; all
	// other actions continue to be governed solely by the advertised capability list.
	if (action === PlanActions.Keep && node.review?.status === 'kept') return true;
	return Boolean(node.allowed_actions?.includes(action));
};

/**
 * The request body for one action on one card, or `null` when the card cannot take it.
 *
 * Returning `null` rather than throwing keeps the decision where the button is drawn: a caller asks
 * once whether it can build the request, and uses the same answer to enable the control and to
 * send it.
 *
 * `client_token` makes the request idempotent — the endpoint stores the response against it and
 * replays that stored response for a retry instead of writing twice, which is what stops a
 * double-submitted sheet creating two events. It must therefore stay the same across retries of one
 * intent and differ between intents; minting it here would defeat that, so the caller supplies it.
 */
export const planActionRequest = (
	node: PlanNodeOut,
	intent: PlanActionIntent,
	clientToken: string,
	/**
	 * Act on a past occurrence rather than the one the card is showing.
	 *
	 * A card names its current occurrence, so a completion recorded against March cannot be undone
	 * from the card once the plan has rolled on to April. Each entry in `occurrences[]` carries its
	 * own token, and passing it here is what lets a History row act on the date it is about.
	 *
	 * Tokens are server-minted and opaque. One the server did not mint is answered with a 422, so
	 * never build one — pass through only what the read handed out.
	 */
	resolveTokenOverride?: string | null,
): PlanActionRequest | null => {
	const resolveToken = resolveTokenOverride || node.action_target?.resolve_token;
	if (!resolveToken || !canSendPlanAction(node, intent.action)) return null;

	const params = paramsFor(intent);
	if (!params) return null;

	return {
		resolve_token: resolveToken,
		action: intent.action,
		params,
		client_token: clientToken,
	};
};

/**
 * What a household said and attached while taking an action.
 *
 * Only occurrence-scoped actions can carry it: evidence is anchored to the occasion, so the
 * endpoint answers evidence on an obligation-level action (`reschedule`, `delete`) with a 422
 * rather than filing it somewhere it does not belong.
 */
export interface PlanActionEvidence {
	comment?: string;
	files?: File[];
}

export const hasEvidence = (evidence?: PlanActionEvidence): boolean =>
	Boolean(evidence?.comment?.trim() || evidence?.files?.length);

/**
 * What makes two sends the same request, as the endpoint decides it.
 *
 * Mirrors `compute_request_fingerprint`: the endpoint hashes the action, the target, the params, the
 * comment and the file names, and holds a `client_token` to the first request it saw under it. The
 * same token with the same fingerprint replays the stored response; the same token with a *different*
 * one is a 409. So a caller reusing a key has to know exactly when the request stopped being the same
 * one, and this is that rule — kept beside the builder so the two cannot drift.
 *
 * `client_token` is excluded, being the thing this decides. Files count by name only, and sorted,
 * because that is what the endpoint hashes.
 */
export const planRequestIdentity = (request: PlanActionRequest, evidence?: PlanActionEvidence): string =>
	JSON.stringify({
		action: request.action,
		comment: evidence?.comment?.trim() || null,
		files: (evidence?.files ?? []).map((file) => file.name).sort(),
		params: Object.entries(request.params).sort(([left], [right]) => left.localeCompare(right)),
		resolve_token: request.resolve_token,
	});

/**
 * The same request as a form, for when it carries a comment or attachments.
 *
 * One endpoint, two transports, chosen by content type — a file-less action is JSON, and evidence
 * makes it multipart. The fields are the same either way except that `params` arrives as a JSON
 * string, because a form has no nesting.
 *
 * Files ride under a repeated `files` key; the server names each one's role by looking at the
 * filename and mime type, so nothing needs to be said about them here.
 */
export const planActionFormData = (request: PlanActionRequest, evidence: PlanActionEvidence): FormData => {
	const form = new FormData();
	form.set('resolve_token', request.resolve_token);
	form.set('action', request.action);
	form.set('params', JSON.stringify(request.params));
	form.set('client_token', request.client_token);

	const comment = evidence.comment?.trim();
	if (comment) form.set('comment', comment);
	for (const file of evidence.files ?? []) form.append('files', file);

	return form;
};
